'use client'
import { Text } from '../../atoms'
import { TextTypes } from '../../../types'
import React, { ReactElement } from 'react'
import { InfoSuccessIcon, InfoWarningIcon, InfoErrorIcon } from '../../icons'

export enum CardTypes {
    SUCCESS = 'success',
    WARNING = 'warning',
    ERROR = 'error',
    DEFAULT = 'default',
}

export enum TypeColors {
    SUCCESS = 'success-100',
    WARNING = 'warning-100',
    ERROR = 'error-100',
    DEFAULT = 'shade-100',
}

const mapTypeToColor = (type: CardTypes): string => {
    switch (type) {
        case CardTypes.SUCCESS:
            return TypeColors.SUCCESS
        case CardTypes.WARNING:
            return TypeColors.WARNING
        case CardTypes.ERROR:
            return TypeColors.ERROR
        default:
            return TypeColors.DEFAULT
    }
}

const mapColorToBackgroundClass = (color: string): string => {
    switch (color) {
        case TypeColors.SUCCESS:
            return 'bg-success-100'
        case TypeColors.WARNING:
            return 'bg-warning-100'
        case TypeColors.ERROR:
            return 'bg-error-100'
        case TypeColors.DEFAULT:
            return 'bg-shade-100'
        default:
            return ''
    }
}

const mapTypeToIcon = (type: CardTypes): ReactElement | undefined => {
    switch (type) {
        case CardTypes.SUCCESS:
            return <InfoSuccessIcon className="text-success-600" />
        case CardTypes.WARNING:
            return <InfoWarningIcon className="text-warning-600" />
        case CardTypes.ERROR:
            return <InfoErrorIcon className="text-error-600" />
        default:
            return
    }
}

export interface CardIndicationProps extends React.HTMLAttributes<HTMLDivElement> {
    type?: CardTypes
    customIcon?: ReactElement
    label: string | number
    description?: string
    color?: string
}

export const CardIndication = ({
    type = CardTypes.DEFAULT,
    customIcon,
    label = '-',
    description,
    color,
    ...rest
}: CardIndicationProps): ReactElement => {
    color = color ?? mapTypeToColor(type)

    const getIcon = () => {
        if (!!customIcon) {
            return customIcon
        }
        if (!!type) {
            return mapTypeToIcon(type)
        }
        return
    }

    const icon: ReactElement | undefined = getIcon()

    const componentClassName = () => {
        const border = 'border border-shade-opacity-200'
        const background = mapColorToBackgroundClass(color)
        const layout = 'p-4 flex flex-col gap-1'

        return `${border} ${background} ${layout} ${rest.className ?? ''}`.trimEnd()
    }

    return (
        <div {...rest} className={componentClassName()}>
            <div className="flex flex-row items-center gap-1">
                <Text type={TextTypes.HEADING_2}>{label}</Text>
                {icon && icon}
            </div>
            {description && <div>{description}</div>}
        </div>
    )
}
